// src/App.js
import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  Link
} from "react-router-dom";

import { db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";

import { CartProvider } from "./context/CartContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { AuthProvider, useAuth } from "./context/AuthContext";

import ScrollToTop from "./components/ScrollToTop"; 
import MobileBottomNav from "./components/MobileBottomNav";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaTools } from "react-icons/fa";

// TEMBEL YÜKLEME (LAZY LOADING) OPERASYONU
const Home = lazy(() => import("./pages/Home"));
const Register = lazy(() => import("./pages/Register"));
const Login = lazy(() => import("./pages/Login"));
const Cart = lazy(() => import("./pages/Cart"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const OrderSummary = lazy(() => import("./pages/OrderSummary"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const Profile = lazy(() => import("./pages/Profile"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const AuthAction = lazy(() => import("./pages/AuthAction")); 
const Shop = lazy(() => import("./pages/Shop"));
const Checkout = lazy(() => import("./pages/Checkout")); 
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const Hakkimizda = lazy(() => import("./pages/Hakkimizda"));
const SSS = lazy(() => import("./pages/SSS"));
const LegalHub = lazy(() => import("./pages/LegalHub")); 
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const OrderSuccess = lazy(() => import("./pages/OrderSuccess"));
const OdemeBasarisiz = lazy(() => import("./pages/OdemeBasarisiz")); 
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const Favorites = lazy(() => import("./pages/Favorites"));

// --- ADMIN SAYFALARI ---
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminComments = lazy(() => import("./pages/admin/AdminComments"));
const AdminStockManager = lazy(() => import("./pages/admin/AdminStockManager"));
const AdminDiscounts = lazy(() => import("./pages/admin/AdminDiscounts"));
const AdminCoupons = lazy(() => import("./pages/admin/AdminCoupons")); 
const AdminStockOut = lazy(() => import("./pages/admin/AdminStockOut")); 
const AdminReturns = lazy(() => import("./pages/admin/AdminReturns")); 
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings")); 
const AdminMailSettings = lazy(() => import("./pages/admin/AdminMailSettings"));
const AdminSliders = lazy(() => import("./pages/admin/AdminSliders"));
const AdminCampaigns = lazy(() => import("./pages/admin/AdminCampaigns"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminProductForm = lazy(() => import("./components/AdminProductForm"));
const AdminProductList = lazy(() => import("./components/AdminProductList"));
const AdminCategoryManager = lazy(() => import("./components/AdminCategoryManager")); 

const injectTrackingScripts = () => {
  if (document.getElementById('ga4-script')) return; 

  const gaScript = document.createElement('script');
  gaScript.id = 'ga4-script';
  gaScript.async = true;
  gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-W68DFBEJDG';
  document.head.appendChild(gaScript);

  const gaInline = document.createElement('script');
  gaInline.id = 'ga4-inline';
  gaInline.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-W68DFBEJDG');
  `;
  document.head.appendChild(gaInline);

  const pixelInline = document.createElement('script');
  pixelInline.id = 'meta-pixel-inline';
  pixelInline.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1562574128874767'); 
    fbq('track', 'PageView');
  `;
  document.head.appendChild(pixelInline);
};

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("cookie_consent");
    if (!consent) {
      setIsVisible(true);
    } else if (consent === "all") {
      injectTrackingScripts();
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem("cookie_consent", "all");
    setIsVisible(false);
    injectTrackingScripts();
  };

  const handleAcceptEssentialOnly = () => {
    localStorage.setItem("cookie_consent", "essential");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-10px_20px_-5px_rgba(0,0,0,0.15)] border-t border-gray-200 z-[9999] px-4 py-4 md:py-5 animate-fade-in-up">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-4">
        <div className="text-xs md:text-sm text-gray-600 leading-relaxed text-center lg:text-left flex-1">
          <strong>Sena Bebe</strong> olarak, sizlere daha iyi hizmet sunabilmek ve alışveriş deneyiminizi kişiselleştirmek amacıyla sitemizde yasal düzenlemelere uygun çerezler (cookies) kullanıyoruz. 
          Çerezleri nasıl kullandığımız hakkında detaylı bilgi için 
          <Link to="/sozlesmeler?tab=gizlilikPolitikasi" className="text-[#6A0DAD] font-bold hover:underline ml-1 mr-1">
            Gizlilik ve Çerez Politikamızı
          </Link> 
          inceleyebilirsiniz.
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto flex-shrink-0">
          <button 
            onClick={handleAcceptEssentialOnly}
            className="w-full sm:w-auto px-6 py-2.5 bg-gray-100 text-gray-600 border border-gray-200 text-xs md:text-sm font-bold rounded-lg hover:bg-gray-200 transition-colors"
          >
            Sadece Zorunlu Olanları Kabul Et
          </button>
          <button 
            onClick={handleAcceptAll}
            className="w-full sm:w-auto px-6 py-2.5 bg-[#6A0DAD] text-white text-xs md:text-sm font-bold rounded-lg shadow-md hover:bg-purple-800 transition-colors"
          >
            Tümünü Kabul Et
          </button>
        </div>
      </div>
    </div>
  );
};

const StrictAdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth(); 
  
  if (loading) {
    return <div className="h-screen flex items-center justify-center font-bold text-[#6A0DAD]">Yetkiler Kontrol Ediliyor...</div>;
  }
  
  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-[#6A0DAD] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function AppWrapper() {
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth(); 
  const [isMaintenance, setIsMaintenance] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "general"), (docSnap) => {
      if (docSnap.exists()) {
        setIsMaintenance(docSnap.data().isMaintenanceMode || false);
      } else {
        setIsMaintenance(false);
      }
    });
    return () => unsub();
  }, []);

  const isAdminRoute = location.pathname.startsWith("/admin");
  const isLoginRoute = location.pathname === "/login";

  const showMaintenanceScreen = isMaintenance && !isAdmin && !isLoginRoute;

  if (showMaintenanceScreen) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center font-sans">
         <div className="w-24 h-24 bg-purple-100 text-[#6A0DAD] rounded-full flex items-center justify-center mb-6 shadow-sm">
            <FaTools size={40} className="animate-pulse" />
         </div>
         <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4 tracking-tight">Sena Bebe Yenileniyor!</h1>
         <p className="text-gray-500 text-sm md:text-base max-w-lg leading-relaxed border-t border-gray-200 pt-6">
            Sizlere çok daha iyi, hızlı ve kusursuz bir alışveriş deneyimi sunabilmek adına altyapımızda kısa süreli bir çalışma yapıyoruz. 
            <br/><br/>
            <strong>Anlayışınız için teşekkür ederiz, çok yakında harika yeniliklerle tekrar buradayız! 💜</strong>
         </p>
      </div>
    );
  }

  return (
    <CartProvider>
      <FavoritesProvider>
        {/* 🔥 FAZ 4: TAŞMA KALKANI BURAYA EKLENDİ! Artık hiçbir sayfa sağa sola sallanamaz. */}
        <div className="w-full max-w-[100vw] overflow-x-hidden flex flex-col min-h-screen">
          <Navbar user={currentUser} />

          <div className="pt-16 p-4 flex-1">
            {isAdminRoute && (
              <h1 className="text-2xl font-bold mb-4 text-center text-gray-800">Sena Bebe Yönetim Paneli</h1>
            )}
            
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* --- KULLANICI TARAFI --- */}
                <Route path="/" element={<Home />} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/auth-action" element={<AuthAction />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/favorites" element={<Favorites />} />

                {/* Shop & İndirimler */}
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/:top" element={<Shop />} />
                <Route path="/shop/:top/:sub" element={<Shop />} />
                <Route path="/indirimler" element={<Shop isDiscountPage={true} />} />

                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/category/:categoryName" element={<CategoryPage />} />

                {/* Sipariş, Ödeme & Profil */}
                <Route path="/summary" element={<OrderSummary />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/odeme-basarili" element={<OrderSuccess />} /> 
                <Route path="/odeme-basarisiz" element={<OdemeBasarisiz />} /> 
                <Route path="/my-orders" element={<MyOrders />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/orders" element={<MyOrders />} />
                <Route path="/orders/:orderId" element={<OrderDetail />} />
                <Route path="/kargo-takip" element={<TrackOrder />} /> 

                {/* Statik Sayfalar */}
                <Route path="/iletisim" element={<ContactPage />} />
                <Route path="/hakkimizda" element={<Hakkimizda />} />
                <Route path="/sss" element={<SSS />} />
                <Route path="/sozlesmeler" element={<LegalHub />} />

                {/* --- ADMIN TARAFI --- */}
                <Route path="/admin" element={<StrictAdminRoute><Navigate to="/admin/orders" replace /></StrictAdminRoute>} />
                <Route path="/admin/orders" element={<StrictAdminRoute><AdminOrders /></StrictAdminRoute>} />
                <Route path="/admin/dashboard" element={<StrictAdminRoute><AdminDashboard /></StrictAdminRoute>} />
                <Route path="/admin/products" element={<StrictAdminRoute><AdminProductList /></StrictAdminRoute>} />
                <Route path="/admin/products/new" element={<StrictAdminRoute><AdminProductForm /></StrictAdminRoute>} />
                <Route path="/admin/categories" element={<StrictAdminRoute><AdminCategoryManager /></StrictAdminRoute>} />
                <Route path="/admin/comments" element={<StrictAdminRoute><AdminComments /></StrictAdminRoute>} />
                <Route path="/admin/stock-manager" element={<StrictAdminRoute><AdminStockManager /></StrictAdminRoute>} />
                <Route path="/admin/stock-out" element={<StrictAdminRoute><AdminStockOut /></StrictAdminRoute>} /> 
                <Route path="/admin/returns" element={<StrictAdminRoute><AdminReturns /></StrictAdminRoute>} />
                
                <Route path="/admin/customers" element={<StrictAdminRoute><AdminCustomers /></StrictAdminRoute>} />

                <Route path="/admin/discounts" element={<StrictAdminRoute><AdminDiscounts /></StrictAdminRoute>} />
                <Route path="/admin/coupons" element={<StrictAdminRoute><AdminCoupons /></StrictAdminRoute>} />
                <Route path="/admin/sliders" element={<StrictAdminRoute><AdminSliders /></StrictAdminRoute>} />
                <Route path="/admin/settings" element={<StrictAdminRoute><AdminSettings /></StrictAdminRoute>} />
                <Route path="/admin/mail-settings" element={<StrictAdminRoute><AdminMailSettings /></StrictAdminRoute>} />
                <Route path="/admin/campaigns" element={<StrictAdminRoute><AdminCampaigns /></StrictAdminRoute>} />
              </Routes>
            </Suspense>
          </div>

          {!isAdminRoute && <CookieBanner />}
          
          <MobileBottomNav />
          <Footer />
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </FavoritesProvider>
    </CartProvider>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <AppWrapper />
      </AuthProvider>
    </Router>
  );
}

export default App;